import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Box,
  IconButton,
  styled,
  alpha,
} from '@mui/material';
import { Upgrade, Close } from '@mui/icons-material';
import GradientButton from '../ui/GradientButton';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    background: theme.palette.mode === 'dark' 
      ? 'rgba(17, 17, 17, 0.95)' 
      : 'rgba(248, 250, 252, 0.95)',
    backdropFilter: 'blur(20px)',
    border: `1px solid ${theme.customPalette?.border?.subtle || alpha(theme.palette.divider, 0.1)}`,
    borderRadius: '16px',
    boxShadow: theme.palette.mode === 'dark'
      ? '0 8px 32px rgba(0, 0, 0, 0.4)'
      : '0 8px 32px rgba(0, 0, 0, 0.1)',
  },
}));

const FeatureList = styled(Box)(({ theme }) => ({
  marginTop: '16px',
  marginBottom: '16px',
  '& .feature-item': {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '12px',
    padding: '8px 12px',
    borderRadius: '8px',
    backgroundColor: theme.palette.mode === 'dark' 
      ? 'rgba(255, 255, 255, 0.05)' 
      : 'rgba(0, 0, 0, 0.02)',
    border: `1px solid ${theme.customPalette?.border?.subtle || alpha(theme.palette.divider, 0.1)}`,
  },
  '& .feature-icon': {
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    backgroundColor: '#FF6B35',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: '12px',
    fontSize: '12px',
    color: 'white',
    fontWeight: 'bold',
  },
}));

interface UpgradeDialogProps {
  open: boolean;
  onClose: () => void;
}

const UpgradeDialog: React.FC<UpgradeDialogProps> = ({ open, onClose }) => {

  const handleGetInTouch = () => {
    window.open('https://info.openana.ai/meetings/ana-s/autonomous_security_engineer', '_blank');
    onClose();
  };

  const features = [
    'Unlimited security scans',
    'Advanced threat modeling',
    'Priority support',
    'Custom integrations',
    'Advanced reporting',
    'Team collaboration tools'
  ];

  return (
    <StyledDialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: '40px',
              height: '40px',
              borderRadius: '12px',
              background: `linear-gradient(135deg, #FF6B35 0%, #F7931E 100%)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Upgrade sx={{ color: 'white', fontSize: '20px' }} />
          </Box>
          <Typography variant="h5" component="span" fontWeight="bold">
            Upgrade to Premium
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1" sx={{ mb: 2 }}>
          Unlock the full potential of OpenANA Security with our premium features.
        </Typography>
        
        <FeatureList>
          {features.map((feature, index) => (
            <div key={index} className="feature-item">
              <div className="feature-icon">✓</div>
              <Typography variant="body2">{feature}</Typography>
            </div>
          ))}
        </FeatureList>

        <Typography variant="body2" color="text.secondary">
          Contact us to learn more about our premium features and pricing plans.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ p: 2.5, pt: 1 }}>
        <Button onClick={onClose} color="inherit">
          Maybe Later
        </Button>
        <GradientButton onClick={handleGetInTouch} variant="contained">
          Get in Touch
        </GradientButton>
      </DialogActions>
    </StyledDialog>
  );
};

export default UpgradeDialog;
